/**
 * RAO-local formatting helpers.
 *
 * Follows feedback_ssn_masking and feedback_date_format_rules:
 *   - SSNs masked as "***-**-1234".
 *   - Dates displayed as `YYYY-MM-DD` when the source is a bare date,
 *     as locale short date + short time when the source is an ISO timestamp.
 */

export function maskSsn(ssn: string | undefined | null): string {
  if (!ssn) return '—';
  const digits = ssn.replace(/\D/g, '');
  if (digits.length < 4) return '***-**-****';
  return `***-**-${digits.slice(-4)}`;
}

export function formatCurrencyUsd(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === '') return '—';
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatIsoDate(iso: string | undefined | null): string {
  if (!iso) return '—';
  // Bare YYYY-MM-DD is parsed as UTC midnight by `new Date(s)`, which shifts
  // to the previous calendar day west of UTC (e.g. Central). Parse bare dates
  // as local midnight instead so DOB renders the stored day.
  const bareDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = bareDate
    ? new Date(
        Number.parseInt(bareDate[1]!, 10),
        Number.parseInt(bareDate[2]!, 10) - 1,
        Number.parseInt(bareDate[3]!, 10),
      )
    : new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatIsoDateTime(iso: string | undefined | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

export function formatRelative(iso: string | undefined | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  return formatIsoDate(iso);
}

/** Build initials (up to 2 letters) from a display name. */
export function initials(name: string | undefined | null): string {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1];
  return ((parts[0][0] ?? '') + (last?.[0] ?? '')).toUpperCase();
}
